import { useToast } from '@shared/components/toast';
import {
  DEFAULT_TOAST_WITH_ACTION_DURATION,
  ToastOptions,
} from '@shared/components/toast/useToast';
import { DeepLinkLocation } from '@shared/constants/identifiers';
import {
  OrderDocument,
  SubmitOrderError,
  TemplateType,
  useSubmitOrderMutation,
} from '@shared/generated/graphql';
import { Order } from '@shared/types/order';
import errorMessages from 'clerk_common/errors/errorMessages.json';
import { isNil } from 'lodash';
import { ReactElement } from 'react';
import { Link } from '..';
import { useAnalytics } from './useAnalytics';
import { useGlossary } from './useGlossary';

type UseSubmitOrderResult = {
  submitOrder: () => Promise<void>;
  isInternalSubmission: boolean;
  hasSubmittedInternally: boolean;
  hasCompletedAction: boolean;
  submitButtonLabel: string;
};

export function useSubmitOrder(order: Order | null): UseSubmitOrderResult {
  const [submitOrder] = useSubmitOrderMutation();
  const { track } = useAnalytics();
  const { sendToast } = useToast();
  const { tms } = useGlossary();
  const isInternalSubmission =
    order !== null &&
    !order.organization.integrationConfigs.integrations.some(
      (config) => config.orderType === TemplateType.FREIGHT_ORDER
    );
  const hasSubmittedInternally =
    isInternalSubmission && (order?.shipmentIds.length ?? 0) > 0;
  const hasCompletedAction =
    (order?.hasBeenSubmitted ?? false) || hasSubmittedInternally;
  const submitButtonLabel = getSubmitButtonLabel({
    hasBeenSubmitted: order?.hasBeenSubmitted ?? false,
    hasSubmittedInternally,
    isInternalSubmission,
    tms,
  });

  const submitOrderWrapper = async (): Promise<void> => {
    if (!order) return;

    const orderId = order.id;
    const response = await submitOrder({
      variables: { input: { id: orderId } },
      refetchQueries: ['GetOrders', OrderDocument],
      awaitRefetchQueries: true,
    }).catch((e) => {
      if (e.message.includes('not live')) {
        sendToast('Integration is not yet live', { isDismissible: true });
        return;
      }
      if (e.message.includes('Geocoding error')) {
        sendToast('Address validation failed', {
          variant: 'error',
          description: errorMessages['errorSupportPostamble'],
          isDismissible: true,
        });
        return;
      }
      // Catch-all case for all other errors.
      sendToast(
        isInternalSubmission
          ? 'Failed to submit order'
          : `Failed to submit order to ${tms}`,
        {
          variant: 'error',
          description: e.message,
          isDismissible: true,
        }
      );
      console.error(e);
      return;
    });

    if (!response?.data?.submitOrder.success) return;

    track('Submitted Order', { orderId });

    const orderLink: string | undefined =
      response?.data?.submitOrder.order?.externalDeepLink ?? undefined;
    const description = !isNil(orderLink) ? (
      <Link
        target="_blank"
        href={orderLink}
        className="text-subtle text-body-sm underline"
        onPress={() =>
          track('Deep Link Clicked', {
            orderId,
            name: DeepLinkLocation.SUBMITTED_ORDER_TOAST,
          })
        }
      >
        View order in {tms}
      </Link>
    ) : undefined;
    if (!isNil(description)) {
      track('Deep Link Viewed', {
        orderId,
        name: DeepLinkLocation.SUBMITTED_ORDER_TOAST,
      });
    }
    sendToast(
      isInternalSubmission ? 'Order submitted' : `Order submitted to ${tms}`,
      {
        variant: 'success',
        isDismissible: true,
        children: description,
        autoHideDuration: DEFAULT_TOAST_WITH_ACTION_DURATION,
      }
    );

    if (!isNil(response.data.submitOrder.errors)) {
      handleSubmitOrderErrors(
        response.data.submitOrder.errors,
        sendToast,
        description
      );
    }
  };

  return {
    submitOrder: submitOrderWrapper,
    isInternalSubmission,
    hasSubmittedInternally,
    hasCompletedAction,
    submitButtonLabel,
  };
}

const getSubmitButtonLabel = ({
  hasBeenSubmitted,
  hasSubmittedInternally,
  isInternalSubmission,
  tms,
}: {
  hasBeenSubmitted: boolean;
  hasSubmittedInternally: boolean;
  isInternalSubmission: boolean;
  tms: string;
}): string => {
  if (isInternalSubmission) {
    return hasSubmittedInternally ? 'Order submitted' : 'Submit Order';
  }
  return hasBeenSubmitted ? `Resend to ${tms}` : `Send to ${tms}`;
};

const handleSubmitOrderErrors = (
  errors: SubmitOrderError[],
  sendToast: (message: string, options: ToastOptions) => void,
  deepLink?: ReactElement
) => {
  if (errors.includes(SubmitOrderError.DOCUMENT_UPLOAD_FAILED)) {
    sendToast('Document upload failure', {
      variant: 'error',
      isDismissible: true,
      description: 'Vooma failed to upload one or more of the order documents.',
      children: deepLink,
    });
  }
};
